import { useState, useCallback } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { python } from '@codemirror/lang-python';
import { oneDark } from '@codemirror/theme-one-dark';
import '../styles/pythonPlayground.css';

const DEFAULT_CODE = `# Write your Python code here
def greet(name):
    return f"Hello, {name}!"

print(greet(name))
`;

const EMPTY_INPUT = () => ({ id: Date.now() + Math.random(), name: '', value: '' });

export default function PythonPlayground({
  height = 480,
  heading = '',
  initialCode = DEFAULT_CODE,
  initialInputs = [{ id: 1, name: 'name', value: '"World"' }],
}) {
  const [code, setCode] = useState(initialCode);
  const [inputs, setInputs] = useState(initialInputs);
  const [output, setOutput] = useState(null);
  const [running, setRunning] = useState(false);
  const [fetchError, setFetchError] = useState('');

  const addInput = () => setInputs((prev) => [...prev, EMPTY_INPUT()]);

  const updateInput = (id, field, value) => {
    setInputs((prev) => prev.map((inp) => (inp.id === id ? { ...inp, [field]: value } : inp)));
  };

  const removeInput = (id) => {
    setInputs((prev) => prev.filter((inp) => inp.id !== id));
  };

  const runCode = useCallback(async () => {
    setRunning(true);
    setOutput(null);
    setFetchError('');
    try {
      const resp = await fetch('/api/python/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          inputs: inputs.map(({ name, value }) => ({ name, value })),
        }),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `Server error ${resp.status}`);
      }
      setOutput(await resp.json());
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setRunning(false);
    }
  }, [code, inputs]);

  const hasStderr = output && (output.exitCode !== 0 || output.stderr);
  const nameColWidth = `${Math.max(8, ...inputs.map((inp) => inp.name.length + 1))}ch`;

  return (
    <div className="py-lab" style={{ minHeight: height }}>

      {/* ── Top bar ── */}
      <div className="py-lab-bar">
        <span className="py-lab-title">Python Lab{heading ? ` — ${heading}` : ''}</span>
        <span className="py-lab-badge">Python 3</span>
        <button
          type="button"
          className="py-run-btn"
          onClick={runCode}
          disabled={running}
        >
          {running ? (
            <><span className="py-spinner" aria-hidden="true" /> Running…</>
          ) : (
            <><span className="py-run-icon" aria-hidden="true">▶</span> Run</>
          )}
        </button>
      </div>

      {/* ── Two-column body ── */}
      <div className="py-lab-body">

        {/* Left: Code editor */}
        <div className="py-editor-col">
          <div className="py-panel-head">
            <span className="py-panel-label">Code</span>
          </div>
          <div className="py-editor-wrap">
            <CodeMirror
              value={code}
              extensions={[python()]}
              theme={oneDark}
              onChange={(val) => setCode(val)}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLine: true,
                foldGutter: false,
                autocompletion: true,
              }}
              style={{ height: '100%' }}
            />
          </div>
        </div>

        {/* Right: Inputs + Output stacked */}
        <div className="py-right-col">

          {/* Input variables */}
          <div className="py-panel py-inputs-panel">
            <div className="py-panel-head">
              <span className="py-panel-label">Input Variables</span>
              <button type="button" className="py-add-btn" onClick={addInput}>+ Add</button>
            </div>
            <div className="py-inputs-body">
              {inputs.length === 0 && (
                <p className="py-empty">No inputs. Click + Add to inject variables.</p>
              )}
              {inputs.map((inp) => (
                <div key={inp.id} className="py-input-row">
                  <input
                    className="py-field py-name"
                    value={inp.name}
                    readOnly
                    style={{ width: nameColWidth }}
                  />
                  <span className="py-eq">=</span>
                  <input
                    className="py-field py-val"
                    placeholder={'"value" or 42 or [1,2,3]'}
                    value={inp.value}
                    onChange={(e) => updateInput(inp.id, 'value', e.target.value)}
                    spellCheck={false}
                  />
                  <button
                    type="button"
                    className="py-remove-btn"
                    onClick={() => removeInput(inp.id)}
                    aria-label="Remove variable"
                  >×</button>
                </div>
              ))}
            </div>
            <p className="py-hint">
              Values are Python expressions: <code>"hello"</code>, <code>42</code>, <code>[1,2,3]</code>
            </p>
          </div>

          {/* Output console */}
          <div className={`py-panel py-output-panel ${hasStderr ? 'py-has-error' : ''}`}>
            <div className="py-panel-head">
              <span className="py-panel-label">Output</span>
              {output && (
                <span className={`py-exit-badge ${output.exitCode === 0 ? 'py-ok' : 'py-fail'}`}>
                  exit {output.exitCode}
                </span>
              )}
              {output && (
                <button type="button" className="py-clear-btn" onClick={() => setOutput(null)}>
                  Clear
                </button>
              )}
            </div>
            <div className="py-console">
              {!output && !fetchError && !running && (
                <span className="py-placeholder">Run your code to see output here…</span>
              )}
              {running && <span className="py-placeholder">Executing…</span>}
              {fetchError && <pre className="py-text py-stderr">{fetchError}</pre>}
              {output && (
                <>
                  {output.stdout && <pre className="py-text py-stdout">{output.stdout}</pre>}
                  {output.stderr && <pre className="py-text py-stderr">{output.stderr}</pre>}
                  {!output.stdout && !output.stderr && (
                    <span className="py-placeholder py-done">✓ Executed with no output</span>
                  )}
                </>
              )}
            </div>
          </div>

        </div>
      </div>


    </div>
  );
}
